import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ROLES, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';

import Category from '../../components/Category/Category';
import { CATEGORIES } from '../../common/categories';
import BulkAssign from '../../../Controls/BulkAssign';
import { AZ_ENTITY_TYPES, fieldsReducer, initFieldsState, mapToSettings } from './availabilityZonesReducer';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import * as actions from './availabilityZonesActions';
import { AVAILABILITY_ZONE_FIELDS, CREATED_AT_FIELD } from '../../common/fields';
import Button from '../../components/Controls/Button/Button';
import AvailabilityZone from '../../components/AvailabilityZone/AvailabilityZone';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';
import { calcDirtyTabs } from '../../common/utils';
import { httpPost, isUserPermitted } from '../../../utils';

const { AVAILABILITY_ZONES } = CATEGORIES;

const AvailabilityZones = ({ saveFunc, disableSave }) => {
    const history = useHistory();
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const [selectNewAzTab, setSelectNewAzTab] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [notEdited, setNotEdited] = useState(true);

    const settings = useSelector((state) => state.settings.settings);
    const searchQuery = useSelector((state) => state.settings.searchQuery);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const { shared, ...fieldsForSearch } = fields;
    const visibleFields = useSearch(fieldsForSearch);

    const [selectedTab, setSelectedTab] = useTabs(AVAILABILITY_ZONES, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);

    useEffect(() => {
        if (selectNewAzTab) {
            const lastTabId = Object.keys(visibleFields).reduce((prev, current) => {
                if (!fields[prev].fields[CREATED_AT_FIELD.ID]) {
                    return current;
                } else if (!fields[current].fields[CREATED_AT_FIELD.ID]) {
                    return prev;
                }
                return fields[prev].fields[CREATED_AT_FIELD.ID].value >
                    fields[current].fields[CREATED_AT_FIELD.ID].value
                    ? prev
                    : current;
            });
            history.push(`/Settings/${AVAILABILITY_ZONES.NAME}#${lastTabId}`);
            setSelectedTab(lastTabId);
            setSelectNewAzTab(false);
        }
    }, [selectNewAzTab, history, visibleFields, fields, setSelectedTab]);

    useEffect(() => {
        setNotEdited(calcDirtyTabs(fields).length === 0);
    }, [setNotEdited, fields]);

    const onBulkAssignClicked = useCallback(() => {
        if (!showBulkAssignModal) {
            setShowBulkAssignModal(true);
        }
    }, [showBulkAssignModal, setShowBulkAssignModal]);

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_AZ });
    }, []);

    const addAz = useCallback(() => {
        dispatch({
            type: actions.ADD_AZ
        });
        setSelectNewAzTab(true);
    }, []);

    const updatedShared = useMemo(() => {
        const newVal = {
            ...shared,
            wallsWithDomains: { ...shared.wallsWithDomains },
            domainOptions: [...shared.domainOptions]
        };
        Object.values(settings.NodesConfigurations).forEach((wall) => {
            if (!newVal.wallsWithDomains[wall.id]) {
                newVal.wallsWithDomains[wall.id] = {
                    id: wall.id,
                    name: wall.Name,
                    domains: Object.keys(wall.domainsConfiguration)
                };
            } else {
                newVal.wallsWithDomains[wall.id] = {
                    ...newVal.wallsWithDomains[wall.id],
                    domains: Object.keys(wall.domainsConfiguration)
                };
            }
        });
        Object.values(settings.DomainsConfigurations).forEach((domain) => {
            const domainOptionIndex = newVal.domainOptions.findIndex(
                (domainOption) => domainOption.value === domain.id
            );
            if (domainOptionIndex === -1) {
                newVal.domainOptions.push({ value: domain.id, label: domain.DomainName });
            } else if (newVal.domainOptions[domainOptionIndex].label !== domain.DomainName) {
                newVal.domainOptions[domainOptionIndex] = {
                    ...newVal.domainOptions[domainOptionIndex],
                    label: domain.DomainName
                };
            }
        });
        return newVal;
    }, [shared, settings.NodesConfigurations, settings.DomainsConfigurations]);

    const dcOptions = useMemo(() => {
        const dcsToAzs = {};
        Object.keys(fieldsForSearch).forEach((azGuid) => {
            fieldsForSearch[azGuid].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value.forEach((dcGuid) => {
                dcsToAzs[dcGuid] = fieldsForSearch[azGuid].fields[AVAILABILITY_ZONE_FIELDS.NAME.ID].value;
            });
        });
        return Object.values(updatedShared.dcsWithDomains).map((value) => {
            return {
                id: value.id,
                name: value.name,
                domain: value.domain,
                az: dcsToAzs[value.id] ? dcsToAzs[value.id] : 'unassigned'
            };
        });
    }, [updatedShared.dcsWithDomains, fieldsForSearch]);

    const wallOptions = useMemo(() => {
        const wallsToAzs = {};
        Object.keys(fieldsForSearch).forEach((azGuid) => {
            fieldsForSearch[azGuid].fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value.forEach((wallGuid) => {
                wallsToAzs[wallGuid] = fieldsForSearch[azGuid].fields[AVAILABILITY_ZONE_FIELDS.NAME.ID].value;
            });
        });
        return Object.values(updatedShared.wallsWithDomains).map((value) => {
            return {
                id: value.id,
                name: value.name,
                domains: value.domains,
                az: wallsToAzs[value.id] ? wallsToAzs[value.id] : 'unassigned'
            };
        });
    }, [updatedShared.wallsWithDomains, fieldsForSearch]);

    const azOptions = useMemo(
        () =>
            Object.keys(fieldsForSearch).map((azGuid) => {
                return {
                    label: fieldsForSearch[azGuid].fields[AVAILABILITY_ZONE_FIELDS.NAME.ID].value,
                    value: azGuid
                };
            }),
        [fieldsForSearch]
    );

    const addAzButton = (
        <Button disabled={searchQuery} style={{ width: '180px' }} onClick={addAz}>
            + Add Availability Zone
        </Button>
    );

    let content = null;
    if (visibleFields[selectedTab]) {
        content = (
            <AvailabilityZone
                azGuid={selectedTab}
                dispatch={dispatch}
                fields={visibleFields[selectedTab].fields}
                shared={updatedShared}
                rerenderOnChange={showBulkAssignModal}
                dcsValue={fields[selectedTab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value}
                protectedDomainsValue={fields[selectedTab].fields[AVAILABILITY_ZONE_FIELDS.PROTECTED_DOMAINS.ID].value}
                sfNodesValue={fields[selectedTab].fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value}
                allowDelete={isEditor}
                disableDelete={!notEdited}
            />
        );
    }

    return (
        <>
            <Category
                category={AVAILABILITY_ZONES}
                fields={fieldsForSearch}
                visibleFields={visibleFields}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                saveSettings={save}
                tabButton={addAzButton}
                actions={[{ name: 'Bulk Assign', clicked: onBulkAssignClicked }]}
                disableSave={disableSave}
                discardAll={() => discard(true)}
                showPerTabButtons={false}
                discard={() => discard(false)}>
                {content}
            </Category>

            {showBulkAssignModal &&
                (notEdited ? (
                    <BulkAssign
                        wallOptions={wallOptions}
                        azOptions={azOptions}
                        dcOptions={dcOptions}
                        getDomainByAzGuid={(azGuid) => {
                            return fields[azGuid].fields[AVAILABILITY_ZONE_FIELDS.PROTECTED_DOMAINS.ID].value;
                        }}
                        getEntityIdsByAzGuid={(azGuid) => {
                            return {
                                dcs: fields[azGuid].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value,
                                walls: fields[azGuid].fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value
                            };
                        }}
                        onCancel={() => {
                            setShowBulkAssignModal(false);
                        }}
                        onSave={(selectedAz, wallsToMove, dcsToMove) => {
                            dispatch({
                                type: actions.MOVE_ENTITIES_TO_AZ,
                                payload: {
                                    entitiesToMove: dcsToMove.map((dc) => dc.id),
                                    azGuid: selectedAz,
                                    type: AZ_ENTITY_TYPES.DC
                                }
                            });
                            dispatch({
                                type: actions.MOVE_ENTITIES_TO_AZ,
                                payload: {
                                    entitiesToMove: wallsToMove.map((wall) => wall.id),
                                    azGuid: selectedAz,
                                    type: AZ_ENTITY_TYPES.WALL
                                }
                            });
                            setShowBulkAssignModal(false);
                        }}
                    />
                ) : (
                    <ConfirmationMessage
                        disableCloseButton={true}
                        title={`bulk assign`}
                        OnApprove={() => setShowBulkAssignModal(false)}
                        body={'Please save your changes before attempting to bulk assign'}
                        OnHide={() => setShowBulkAssignModal(false)}
                        hideCancelButton={true}
                    />
                ))}
        </>
    );
};
export default AvailabilityZones;
